import { useMemo } from 'react';
import { AmplitudeFeatureFlags, AmplitudeFeatureFlagType } from '@sweatpals/shared';
import { useAmplitudeContext } from '../../contexts';

/**
 * A hook that retrieves the value of an Amplitude feature flag.
 *
 * @param featureFlagName - The name of the feature flag to retrieve
 * @param defaultValue - Optional default value to return if the flag is not found
 * @returns The current value of the feature flag, default value if provided, or undefined
 *
 * @example
 * ```tsx
 * const isFeatureEnabled = useAmplitudeFeatureFlag('my_feature_flag');
 *
 * // With default value
 * const isFeatureEnabled = useAmplitudeFeatureFlag('my_feature_flag', 'off');
 *
 * if (isFeatureEnabled === 'on') {
 *   // Feature is enabled
 * }
 * ```
 *
 * @note This hook can only be used within the scope of an AmplitudeContext provider
 */
export const useAmplitudeFeatureFlag = <T extends AmplitudeFeatureFlags>(
	featureFlagName: T,
	defaultValue?: AmplitudeFeatureFlagType<T>
): AmplitudeFeatureFlagType<T> | undefined => {
	const { experimentClientRef, latestExperimentsFetch, currentUser } = useAmplitudeContext();

	return useMemo(() => {
		if (!experimentClientRef?.current) {
			return defaultValue;
		}

		const currentVariant = experimentClientRef?.current?.variant(featureFlagName)?.value as unknown as AmplitudeFeatureFlagType<T>;
		console.info('[Amplitude] refreshed feature flag value: ', { featureFlagName, currentVariant });

		return currentVariant !== undefined ? currentVariant : defaultValue;
	}, [
		experimentClientRef,
		experimentClientRef?.current,
		featureFlagName,
		currentUser?.id,
		latestExperimentsFetch?.current,
		defaultValue
	]);
}
